import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {UnaryMathOperatorOption} from "#template/nodes/geometry-graph/options"
import {unaryMathOperatorOption} from "#template/nodes/geometry-graph/options"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type UnaryMathOperatorParameters<TAttribute> = {
    arg: TAttribute | null
    operator: UnaryMathOperatorOption
}

export const unaryMathOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        arg: z.nullable(attributeSchema),
        operator: unaryMathOperatorOption,
    })

function applyUnaryMathOperator<T extends IndexingDim>(argAttribute: GeometryAttributeData<T>, operator: UnaryMathOperatorOption): GeometryAttributeData<T> {
    const argAttrRefData = argAttribute.ref

    const unaryMathAttrRefData = applyGeometryOperator((arg: AttributeRef) => {
        switch (operator) {
            case "not":
                return arg.not()
            case "sin":
                return arg.sin()
            case "cos":
                return arg.cos()
            case "norm":
                return arg.norm()
            case "normalize":
                return arg.normalize()
            case "sqrt":
                return arg.sqrt()
            case "recip":
                return arg.recip()
            case "recipSqrt":
                return arg.recipSqrt()
            case "exp":
                return arg.exp()
            case "log":
                return arg.log()
            case "neg":
                return arg.neg()
            default:
                throw new Error(`Unknown unary math operator: ${operator}`)
        }
    }, argAttrRefData)

    return createGeometryAttributeData(unaryMathAttrRefData, argAttribute.dim)
}

export abstract class GeometryUnaryMathOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, UnaryMathOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: UnaryMathOperatorParameters<TAttribute>,
    ): parameters is UnaryMathOperatorParameters<TAttribute> & NonNullableParameters<UnaryMathOperatorParameters<TAttribute>> {
        return parameters.arg !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<UnaryMathOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {arg, operator: operatorName} = parameters
        const [evaluatedArgValid, evaluatedArgInvalid] = scope.branch(evaluator.evaluate(arg))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedArgValid, operatorName),
            ([arg, operatorName]) => applyUnaryMathOperator(arg, operatorName),
            "applyUnaryMathOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: evaluatedArgInvalid}
    }
}
